import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Users, Bed, Building2, Loader } from 'lucide-react';
import { apartmentService } from '../services/api';
import { ApartmentSummary } from '../types';

const HomePage: React.FC = () => {
  const [apartments, setApartments] = useState<ApartmentSummary[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchApartments = async () => {
      try {
        const response = await apartmentService.getAll({ per_page: 50 });
        setApartments(response.data.apartments || []);
      } catch (error) {
        console.error('Erreur chargement appartements:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchApartments();
  }, []);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('fr-FR').format(price);
  };

  const getImageUrl = (apartment: ApartmentSummary) => {
    if (apartment.photo_principale?.url) {
      const baseUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';
      return `${baseUrl}${apartment.photo_principale.url}`;
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Header */}
      <div className="bg-white border-b border-neutral-200">
        <div className="container mx-auto px-4 py-8">
          <h1 className="text-3xl md:text-4xl font-bold text-neutral-900 mb-2">
            Appartements disponibles
          </h1>
          <p className="text-neutral-600">
            {loading ? 'Chargement...' : `${apartments.length} appartement${apartments.length > 1 ? 's' : ''} disponible${apartments.length > 1 ? 's' : ''}`}
          </p>
        </div>
      </div>

      {/* Liste des appartements */}
      <div className="container mx-auto px-4 py-8">
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <Loader className="w-10 h-10 animate-spin text-primary-500" />
          </div>
        ) : apartments.length === 0 ? (
          <div className="text-center py-20">
            <Building2 className="w-20 h-20 text-neutral-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-neutral-700 mb-2">Aucun appartement disponible</h3>
            <p className="text-neutral-500">Revenez plus tard pour découvrir nos offres.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {apartments.map((apartment) => (
              <Link
                key={apartment.id}
                to={`/apartments/${apartment.id}`}
                className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow duration-300 group"
              >
                {/* Photo */}
                <div className="aspect-[4/3] relative overflow-hidden bg-neutral-200">
                  {getImageUrl(apartment) ? (
                    <img
                      src={getImageUrl(apartment)!}
                      alt={apartment.titre}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-neutral-400">
                      <Building2 className="w-12 h-12" />
                    </div>
                  )}
                  {/* Badge prix */}
                  <div className="absolute bottom-3 left-3 bg-white/95 backdrop-blur-sm px-3 py-1.5 rounded-lg shadow">
                    <span className="font-bold text-primary-600">{formatPrice(apartment.prix_par_nuit)}</span>
                    <span className="text-neutral-500 text-sm"> FCFA/nuit</span>
                  </div>
                </div>

                {/* Infos */}
                <div className="p-4">
                  <h3 className="font-semibold text-lg text-neutral-800 mb-2 truncate group-hover:text-primary-600 transition-colors">
                    {apartment.titre}
                  </h3>
                  
                  <div className="flex items-center text-neutral-500 text-sm mb-3">
                    <MapPin className="w-4 h-4 mr-1" />
                    <span>{apartment.ville}</span>
                  </div>

                  <p className="text-neutral-600 text-sm mb-4 line-clamp-2">
                    {apartment.description_courte}
                  </p>

                  <div className="flex items-center gap-4 text-sm text-neutral-500 pt-3 border-t border-neutral-100">
                    <div className="flex items-center">
                      <Users className="w-4 h-4 mr-1" />
                      <span>{apartment.capacite} pers.</span>
                    </div>
                    <div className="flex items-center">
                      <Bed className="w-4 h-4 mr-1" />
                      <span>{apartment.nb_chambres} ch.</span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default HomePage;
